import React, { useEffect } from 'react';
import * as PropTypes from 'prop-types';
import cn from 'classnames';
import get from 'lodash/get';

import { DEPENDENT_FORM_STEPS } from '../../../redux/dependentForm/progress.actions';
import { useCountrySelect } from '../../../hocs/useCountrySelect';
import deepTrim from '../../../utils/converters/deepTrim';
import { IDS } from '../../../constants/ids';
import AvatarWithCropModal from '../../common/AvatarWithCropModal';
import Button from '../../Button';
import {
  useGeneralInfoFormState,
  useAvatarState,
} from '../utils/useGeneralInfoFormState';
import GeneralInfoContactForm from './GeneralInfoContactForm';
import GeneralInfoAddressForm from './GeneralInfoAddressForm';
import { enhance } from './enhance';
import { usePageView } from '../../../hocs/usePageView';
import MetaHelmet from '../../AppHelmet/MetaHelmet';

const DependentGeneralInfo = ({
  dataValidationErrors: errors,
  formatMessage,
  goNext,
  pending,
  dependentInfo,
  assignmentId,
  fetchAssignment,
  clearAssignment,
  dependent,
  registrationSteps,
  titleWithRole,
  countryList,
}) => {
  usePageView('Add Dependent | General Info');

  useEffect(() => {
    if (registrationSteps[DEPENDENT_FORM_STEPS.GENERAL])
      fetchAssignment({ id: assignmentId });
    return () => clearAssignment();
  }, []);

  const { handleImageRemove, handleImageCrop, avatar } = useAvatarState(
    dependentInfo,
  );

  const {
    loadCountryDetails,
    stateList,
    zipLimit,
    isZipApplicable,
    zipExamples,
  } = useCountrySelect(formatMessage);

  const {
    handleFormChange,
    validateErrors,
    setCountryValue,
    clearErrors,
    formState,
    isValid,
  } = useGeneralInfoFormState(
    dependent || dependentInfo,
    errors,
    isZipApplicable,
  );

  useEffect(() => {
    const {
      city,
      postalCode,
      address1,
      country,
      state,
      email,
      phoneNumber,
      emailFake,
    } = dependent;
    handleFormChange({
      ...formState,
      ...(dependent && {
        ...(city && { city }),
        ...(postalCode && { postalCode }),
        ...(address1 && { address1 }),
        ...(!emailFake && email && { email }),
        phoneNumber,
        ...(state && { state: { value: state, label: state } }),
        ...(country && { country: { value: country, label: country } }),
      }),
    });
  }, [dependent]);

  const onCountryChange = data => {
    clearErrors();
    loadCountryDetails(data.value);
    setCountryValue(data);
  };

  useEffect(() => {
    clearErrors();
  }, []);

  const handleSubmit = e => {
    e.preventDefault();

    const fullCountryName = get(formState, 'country.label', '');
    const fullStateName = get(formState, 'state.label', '');

    clearErrors();
    goNext({
      step: DEPENDENT_FORM_STEPS.GENERAL,
      data: deepTrim({
        ...formState,
        country: fullCountryName,
        state: fullStateName,
        avatar,
      }),
    });
  };

  return (
    <div styleName="section">
      <MetaHelmet title="pages.title.dependentGeneral" />
      {titleWithRole}

      <div styleName={cn('content', { disabled: pending })}>
        <form onSubmit={handleSubmit} className="flex flex-column">
          <label styleName="form--label">
            {formatMessage({ id: 'profile.section.general' })}
          </label>

          <div styleName="avatar">
            <AvatarWithCropModal
              size={96}
              onImageRemove={handleImageRemove}
              onImageCrop={handleImageCrop}
              edit
              formatMessage={formatMessage}
              url={avatar}
              offsetX={20}
            />
          </div>

          <GeneralInfoContactForm
            {...formState}
            validateErrors={validateErrors}
            formatMessage={formatMessage}
            onChange={handleFormChange}
          />

          <div styleName="address-form">
            <label styleName="form--label">
              {formatMessage({ id: 'profile.section.address' })}
            </label>
            <GeneralInfoAddressForm
              {...formState}
              validateErrors={validateErrors}
              onCountryChange={onCountryChange}
              zipLimit={zipLimit}
              isZipApplicable={isZipApplicable}
              formatMessage={formatMessage}
              onChange={handleFormChange}
              statesList={stateList}
              countryList={countryList}
              zipExamples={zipExamples}
              loadCountryDetails={loadCountryDetails}
            />
          </div>

          <div styleName="btn-block">
            <Button
              className="mr3"
              id={IDS.ADD_DEPENDENT.NEXT}
              type="submit"
              raised
              color="primary"
              disabled={!isValid || pending}
            >
              {formatMessage({ id: 'common.next' })}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

DependentGeneralInfo.propTypes = {
  dataValidationErrors: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  dependentInfo: PropTypes.object,
  titleWithRole: PropTypes.node,
  formatMessage: PropTypes.func,
  goNext: PropTypes.func,
  pending: PropTypes.bool,
  assignmentId: PropTypes.number,
  fetchAssignment: PropTypes.func.isRequired,
  clearAssignment: PropTypes.func.isRequired,
  dependent: PropTypes.object,
  registrationSteps: PropTypes.object,
  statesList: PropTypes.array,
  countryList: PropTypes.array,
};

export default enhance(DependentGeneralInfo);



// WEBPACK FOOTER //
// ./src/components/DependentForm/DependentGeneralInfo/index.jsx